import { createApp } from 'vue'
import { createPinia } from 'pinia'
import { VueQueryPlugin } from '@tanstack/vue-query'
import PrimeVue from 'primevue/config'
import Aura from '@primevue/themes/aura'
import ToastService from 'primevue/toastservice'
import ConfirmationService from 'primevue/confirmationservice'
import Tooltip from 'primevue/tooltip'

import 'primeicons/primeicons.css'
import 'bootstrap/js/dist/collapse'
import 'bootstrap/js/dist/modal'

import App from './App.vue'
import router from './router'
import i18n from './i18n'
import './style.css'
import './styles/onboarding.css'
import './utils/css-debug' // Import CSS debugging utilities

const app = createApp(App)

// --- Remote error reporting ---
import { API_BASE_URL } from './api/client'
const ERROR_API = API_BASE_URL + '/api/errors'

function reportError(payload: Record<string, unknown>) {
  try {
    navigator.sendBeacon(ERROR_API, new Blob(
      [JSON.stringify({ source: 'admin', url: window.location.href, ...payload })],
      { type: 'application/json' }
    ))
  } catch { /* best-effort */ }
}

app.config.errorHandler = (err, instance, info) => {
  const error = err instanceof Error ? err : new Error(String(err))
  console.error('[admin] Vue error:', error)
  reportError({
    message: error.message,
    stack: error.stack,
    component: instance?.$options?.name || (instance?.$options as any)?.__name || 'unknown',
    extra: { info }
  })
}

window.addEventListener('unhandledrejection', (event) => {
  const reason = event.reason
  console.error('[admin] Unhandled promise rejection:', reason)
  reportError({
    message: reason instanceof Error ? reason.message : String(reason),
    stack: reason instanceof Error ? reason.stack : undefined,
    error: 'unhandledrejection'
  })
})

window.addEventListener('error', (event) => {
  console.error('[admin] Uncaught error:', event.message)
  reportError({
    message: event.message,
    stack: `${event.filename}:${event.lineno}:${event.colno}`,
    error: 'uncaught'
  })
})

const pinia = createPinia()

app.use(pinia)
app.use(i18n)
app.use(VueQueryPlugin, {
  queryClientConfig: {
    defaultOptions: {
      queries: {
        refetchOnWindowFocus: false,
        retry: 1,
        staleTime: 5 * 60 * 1000, // 5 minutes
      },
    },
  },
})
app.use(router)
app.use(PrimeVue, {
  theme: {
    preset: Aura,
    options: {
      prefix: 'p',
      darkModeSelector: '.app-dark',
      cssLayer: false
    }
  }
})
app.use(ToastService)
app.use(ConfirmationService)
app.directive('tooltip', Tooltip)

// Initialize auth store (validate stored token) before mounting
import { useAuthStore } from './stores/auth'
const authStore = useAuthStore()
authStore.initialize().finally(() => {
  app.mount('#app')
})
